import { motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"

type Direction = "left" | "right" | "bottom" | "top"

interface SlamBlockProps {
  children: ReactNode
  from?: Direction
  delay?: number
  className?: string
}

const OFFSETS: Record<
  Direction,
  { x?: number; y?: number; rotate?: number; scale?: number }
> = {
  left: { x: -100, rotate: -3 },
  right: { x: 100, rotate: 3 },
  bottom: { y: 80 },
  top: { y: -80, scale: 1.1 },
}

/**
 * Dramatic slam-in entrance with spring overshoot — like a Blood Bowl block.
 * Fires once when the element enters the viewport.
 * Degrades to an opacity-only fade (no spring, no displacement) when the user
 * prefers reduced motion.
 */
export function SlamBlock({
  children,
  from = "bottom",
  delay = 0,
  className,
}: SlamBlockProps) {
  const reduceMotion = useReducedMotion()
  const offset = reduceMotion ? {} : OFFSETS[from]

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, ...offset }}
      whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={
        reduceMotion
          ? { duration: 0.4, ease: "easeOut", delay }
          : {
              opacity: { duration: 0.4, ease: "easeOut", delay },
              default: {
                type: "spring",
                stiffness: 200,
                damping: 18,
                delay,
              },
            }
      }
    >
      {children}
    </motion.div>
  )
}
